import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy, Loader2, Radio, Zap, Trophy, Hash, ArrowRight,
  CheckCircle2, Film, PlaySquare,
} from "lucide-react";

/*  STREAMER PLAYBOOK — a one-page go-live checklist + title/caption library.
    Pass ?code=STRMCODE to personalize with the streamer's affiliate links.
    No auth gating — this is a sales / activation page as much as a tool.
*/
const StreamerPlaybook = () => {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").toUpperCase();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/streamer/playbook" + (code ? `?code=${encodeURIComponent(code)}` : ""))
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load the playbook. Try reloading."))
      .finally(() => setLoading(false));
  }, [code]);

  const copy = async (text, label = "Copied.") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Copy blocked by this browser.");
    }
  };

  if (loading) {
    return (
      <div className="grain min-h-screen bg-[#0a0908] flex items-center justify-center text-[#a39797]">
        <Loader2 size={16} className="animate-spin" /> &nbsp; Loading the playbook…
      </div>
    );
  }
  if (!data) return null;

  const { playbook } = data;

  return (
    <div className="grain min-h-screen bg-[#0a0908] text-[#f4f0ec]">
      {/* Header */}
      <div className="border-b border-[#2a2525] px-5 py-4 flex items-center justify-between">
        <Link to="/" className="text-[10px] tracking-[0.3em] text-[#d4af37]" data-testid="playbook-logo">
          ◉ WASNTME.PARTY
        </Link>
        <div className="text-[9px] tracking-[0.3em] text-[#5a5050]">STREAMER PLAYBOOK</div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <h1 className="font-display text-4xl sm:text-5xl mb-3 leading-[1.05]">
          Go live with a <span className="text-[#d4af37]">murder-mystery</span><br/>
          your chat can actually win.
        </h1>
        <p className="text-sm text-[#a39797] mb-7 leading-relaxed max-w-2xl">
          Follow this. Your chat votes on a live case, you reveal when the vote goal hits, and
          every pack their crew buys through your link earns you 15%.
        </p>

        {/* Your links */}
        <div className="card-noir p-5 mb-7" data-testid="playbook-links">
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">YOUR LINKS {data.code !== "YOURCODE" ? `· ${data.code}` : "· (apply for an affiliate code first)"}</div>
          <LinkRow label="OBS Browser Overlay"    url={playbook.overlay_url}    onCopy={() => copy(playbook.overlay_url, "Overlay URL copied.")} testid="playbook-overlay" />
          <LinkRow label="Chat Vote Landing"       url={playbook.landing_url}    onCopy={() => copy(playbook.landing_url, "Vote URL copied.")}   testid="playbook-landing" />
          <LinkRow label="Your Packs Affiliate"    url={playbook.packs_url}      onCopy={() => copy(playbook.packs_url, "Affiliate link copied.")} testid="playbook-packs" />
          {data.code === "YOURCODE" && (
            <div className="mt-3 text-[11px] text-[#a39797]">
              <Link to="/streamer" className="text-[#d4af37] underline">Apply for a free affiliate code →</Link>
            </div>
          )}
        </div>

        {/* 3-column: pre / during / post */}
        <div className="grid md:grid-cols-3 gap-4 mb-9">
          <Phase title="BEFORE YOU GO LIVE" Icon={Zap}       steps={playbook.pre_live}    testid="playbook-pre" />
          <Phase title="WHILE LIVE"         Icon={Radio}     steps={playbook.during_live} testid="playbook-during" />
          <Phase title="AFTER THE STREAM"   Icon={Trophy}    steps={playbook.post_live}   testid="playbook-post" />
        </div>

        {/* Title formulas */}
        <div className="card-noir p-5 mb-7" data-testid="playbook-titles">
          <div className="flex items-center gap-2 mb-3">
            <PlaySquare size={13} className="text-[#d4af37]" />
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">STREAM TITLE FORMULAS</div>
          </div>
          <div className="space-y-2">
            {playbook.title_formulas.map((t, i) => (
              <div key={i} className="flex items-center justify-between gap-3 border border-[#2a2525] bg-[#1a1515] rounded-md px-3 py-2">
                <div className="text-xs text-[#d4d0cc] break-words">{t}</div>
                <button onClick={() => copy(t, "Title copied.")}
                  className="text-[10px] text-[#a39797] hover:text-[#d4af37] shrink-0"
                  data-testid={`playbook-title-copy-${i}`}>
                  <Copy size={11} className="inline mr-1" /> Copy
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Hashtags */}
        <div className="card-noir p-5 mb-10" data-testid="playbook-hashtags">
          <div className="flex items-center gap-2 mb-3">
            <Hash size={13} className="text-[#d4af37]" />
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">HASHTAG PACK</div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {playbook.hashtags.map((h) => (
              <span key={h} className="text-[11px] px-2 py-1 rounded-full border border-[#2a2525] bg-[#1a1515] text-[#d4d0cc]">
                {h}
              </span>
            ))}
          </div>
          <button onClick={() => copy(playbook.hashtags.join(" "), "Hashtags copied.")}
            className="btn-ghost text-xs" data-testid="playbook-hashtags-copy">
            <Copy size={11} /> Copy all
          </button>
        </div>

        {/* Next steps */}
        <div className="flex flex-wrap gap-2">
          <Link to={`/streamer/outreach${data.code !== "YOURCODE" ? `?code=${data.code}` : ""}`}
            className="btn-primary text-sm" data-testid="playbook-cta-outreach">
            <Film size={13} /> Get the outreach kit <ArrowRight size={12} />
          </Link>
          <Link to="/streamer/dashboard" className="btn-ghost text-xs" data-testid="playbook-cta-dashboard">
            Your dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

const LinkRow = ({ label, url, onCopy, testid }) => (
  <div className="flex items-center justify-between gap-3 py-2.5 border-b border-[#2a2525] last:border-b-0">
    <div className="min-w-0 flex-1">
      <div className="text-[10px] tracking-[0.2em] text-[#a39797] mb-0.5">{label}</div>
      <div className="text-xs text-[#d4d0cc] truncate font-mono" data-testid={`${testid}-url`}>{url}</div>
    </div>
    <button onClick={onCopy} className="text-[10px] text-[#d4af37] hover:text-[#f4f0ec] shrink-0" data-testid={`${testid}-copy`}>
      <Copy size={11} className="inline mr-1" /> Copy
    </button>
  </div>
);

const Phase = ({ title, Icon, steps, testid }) => (
  <div className="card-noir p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={13} className="text-[#d4af37]" />
      <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">{title}</div>
    </div>
    <ul className="space-y-2.5">
      {steps.map((s) => (
        <li key={s.id} className="flex gap-2 items-start">
          <CheckCircle2 size={12} className="text-[#8b0000] mt-0.5 shrink-0" />
          <span className="text-[11px] text-[#d4d0cc] leading-relaxed">{s.label}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default StreamerPlaybook;
